/**
 * **Eine Datei an eine Aufgabe oder ein Vorhaben hängen** (Phase 9, Konzept Kap. 4.7;
 * seit dem 24.08.2026 nicht mehr nur Bilder, [E-217](../../../../docs/ENTSCHEIDUNGEN.md#e-217)).
 *
 * Steht nicht in `trpc.ts`, weil es kein tRPC-Aufruf ist: eine Datei geht als roher Körper
 * über die Leitung, nicht als JSON. Der Grund ist derselbe wie beim Ausliefern – base64
 * kostet ein Drittel mehr Bytes für nichts.
 *
 * Der Dateiname reist in einer Kopfzeile. `encodeURIComponent`, weil HTTP-Kopfzeilen nur
 * ASCII tragen: ein Screenshot heißt auf einem deutschen Rechner regelmäßig
 * `Bildschirmfoto 2026-08-05 um 14.31.12.png`, und ein Umlaut darin bricht die Anfrage
 * ohne verständliche Meldung ab. Die Gegenseite dekodiert ihn wieder (`dateinameEntziffern`).
 */
export interface HochgeladenerAnhang {
  id: string
  dateiname: string
  /**
   * **Am Inhalt bestimmt, nicht an der Endung** – deshalb kommt er vom Server zurück und
   * wird hier nicht aus `File.type` geraten. Er entscheidet, ob der Verweis im Text ein
   * Bild wird oder ein Download-Link (`anhangVerweis`).
   */
  mimeTyp: string
}

/**
 * **Woran der Anhang hängen soll** (21.08.2026): an einer Aufgabe oder an einem Vorhaben.
 *
 * Als Objekt mit genau einem Feld und nicht als zwei Zeichenketten-Parameter: `hochladen(id,
 * 'vorhaben')` liest sich an der Aufrufstelle wie ein Rätsel, und zwei optionale Kennungen
 * ließen sich beide leer lassen.
 */
export type AnhangZiel = { aufgabeId: string } | { vorhabenId: string } | { dokumentId: string }

export async function anhangHochladen(ziel: AnhangZiel, datei: File): Promise<HochgeladenerAnhang> {
  const pfad =
    'aufgabeId' in ziel
      ? `/api/anhang/aufgabe/${ziel.aufgabeId}`
      : 'vorhabenId' in ziel
        ? `/api/anhang/vorhaben/${ziel.vorhabenId}`
        : /** Die Ablage (Z-14) – ein Bild im Text oder die Datei selbst als neue Fassung. */
          `/api/anhang/dokument/${ziel.dokumentId}`

  const antwort = await fetch(pfad, {
    method: 'POST',
    /** Ohne die Sitzung kommt die Anfrage als „nicht angemeldet" an. */
    credentials: 'include',
    headers: {
      /**
       * **Bilder unter ihrem Typ, alles andere als `application/octet-stream`**
       * (24.08.2026). Der Server hat für genau diese beiden einen Parser; ein PDF unter
       * `application/pdf` liefe gegen „Unsupported Media Type", bevor die Route läuft.
       *
       * Ein Verlust ist das nicht: welcher Typ es wirklich ist, bestimmt der Server ohnehin
       * neu – die Angabe des Browsers stammt bei einer `.msg` aus einer Endungstabelle des
       * Betriebssystems und ist an manchen Rechnern schlicht leer.
       */
      'Content-Type': datei.type.startsWith('image/') ? datei.type : 'application/octet-stream',
      'X-Dateiname': encodeURIComponent(datei.name),
    },
    body: datei,
  })

  const text = await antwort.text()
  if (!antwort.ok) {
    /**
     * Die Meldung des Servers durchreichen, wenn es eine gibt – „Die Datei ist 12,4 MB
     * groß" ist eine Auskunft, „400" ist keine.
     */
    let grund = `Hochladen fehlgeschlagen (${antwort.status})`
    try {
      const koerper = JSON.parse(text) as { fehler?: string }
      if (koerper.fehler) grund = koerper.fehler
    } catch {
      /* Kein JSON – dann bleibt es beim Statuscode. */
    }
    throw new Error(grund)
  }

  return JSON.parse(text) as HochgeladenerAnhang
}

/**
 * **Der Verweis, der in den Entwurf geschrieben wird** (24.08.2026).
 *
 * Ein Bild wird angezeigt (`![](anhang:…)`), eine Datei bekommt einen Download-Link mit
 * ihrem Namen (`[Angebot.pdf](anhang:…)`). Beide Formen kann `markdown.tsx` seit
 * [E-201](../../../../docs/ENTSCHEIDUNGEN.md) darstellen – bis hierher entstand nur die
 * erste, weil nur Bilder hochgeladen werden konnten.
 *
 * **Eckige Klammern im Namen werden ersetzt.** `[Bericht [final].pdf](anhang:…)` endet die
 * Klammer zu früh, und im Text stünde danach ein Rest, den niemand geschrieben hat.
 */
export function anhangVerweis(anhang: HochgeladenerAnhang): string {
  if (anhang.mimeTyp.startsWith('image/')) return `![](anhang:${anhang.id})`
  return `[${anhang.dateiname.replace(/[[\]]/g, '')}](anhang:${anhang.id})`
}

/**
 * **Was aus einer Ablage- oder Einfüge-Aktion herauszuholen ist.**
 *
 * Zwei Wege, ein Ergebnis: Ziehen liefert `dataTransfer.files`, Einfügen aus der
 * Zwischenablage liefert `clipboardData.items`. Der zweite ist für Bilder der wichtigere –
 * ein Screenshot entsteht mit ⌘⇧4 und landet in der Zwischenablage, nicht als Datei auf dem
 * Schreibtisch.
 *
 * **Seit dem 24.08.2026 ohne Filter auf `image/`** ([E-217](../../../../docs/ENTSCHEIDUNGEN.md#e-217)).
 * Vorher wurde eine gezogene PDF hier stillschweigend weggeworfen: kein Upload, keine
 * Meldung, nichts. Was Work nicht annimmt, sagt jetzt der Server – mit einem Satz, der den
 * Grund nennt („Dateien mit der Endung .exe nimmt Work nicht an.").
 *
 * `kind === 'file'` bleibt: beim Einfügen von Text aus einer Textverarbeitung stehen in
 * `items` mehrere Einträge, und die mit `kind === 'string'` sind der Text selbst.
 */
/**
 * **Zieht da gerade jemand Dateien über das Fenster?** (24.08.2026)
 *
 * ## Warum das nicht mit `dateienAus` zu beantworten ist
 *
 * Während `dragover` läuft, steht der Inhalt **noch nicht** zur Verfügung: der Browser hält
 * die Zwischenablage im „protected mode", `dataTransfer.files` ist leer und `getAsFile()`
 * gibt `null` zurück. Erst beim Loslassen gibt er die Daten frei.
 *
 * Das war bis heute ein echter Fehler und nicht bloß eine Feinheit: Der Ablagebereich fragte
 * in `onDragOver` nach den Bildern, bekam nichts, und rief deshalb **`preventDefault()`
 * nicht** auf. Ohne das ist ein Element aber gar kein gültiges Ziel – der Browser feuert
 * `drop` nicht, sondern öffnet die Datei in einem neuen Tab. **Ziehen hat also nie
 * funktioniert**, auch nicht mit Bildern; gemeldet hat es niemand, weil Einfügen mit ⌘V den
 * Weg abdeckte, den man täglich braucht.
 *
 * `types` ist die eine Auskunft, die in dieser Phase erlaubt ist. `'Files'` steht darin,
 * sobald etwas aus dem Dateisystem am Zeiger hängt – und nur dann; markierter Text meldet
 * `text/plain`.
 */
export function ziehtDateien(daten: DataTransfer | null): boolean {
  return [...(daten?.types ?? [])].includes('Files')
}

export function dateienAus(daten: DataTransfer | null): File[] {
  if (!daten) return []
  const ausDateien = [...daten.files]
  if (ausDateien.length > 0) return ausDateien
  return [...daten.items]
    .filter((i) => i.kind === 'file')
    .map((i) => i.getAsFile())
    .filter((f): f is File => f !== null)
}
